// Boots the web side of the native-app bridge.
//
// This used to be five modules under frontend/native_app/javascripts/boot/, every
// one of them written against the `up` global. It lives in frontend/shared now
// because the Inertia frontend needs the same bridge and must not touch `up` (see
// CLAUDE.md); the two behaviours that really are frontend-specific are injected as
// a `NativeAppNavigation` adapter.
//
// Callers:
//   - frontend/native_app/javascripts/boot/native_app_bridge.ts (Unpoly)
//   - frontend/inertia/native_app.ts (Inertia)

import { on } from 'shared/javascripts/util/events'
import { installStudyflixApp, postNativeMessage, studyflixWeb } from './messages'
import { interceptExternalLinks } from './external_links'
import { NativeAppRouter, type NativeAppNavigation } from './navigation'

export interface NativeAppBridge {
  router: NativeAppRouter
  /** Unregisters everything this bridge installed. Both frontends boot once and never
   * tear down; it exists so specs can start from a clean document. */
  destroy(): void
}

export function initNativeAppBridge(navigation: NativeAppNavigation): NativeAppBridge {
  installStudyflixApp()
  const router = new NativeAppRouter(navigation)

  // This should already be the default for a newly started app, but just to be safe.
  postNativeMessage({ command: 'setOrientation', value: 'portrait' })

  const stopListeningForTabTaps = on(
    document,
    'StudyflixApp.navigationBarTapped',
    (event: CustomEvent<{ key: string }>) => {
      router.goTo(event.detail.key)
    },
  )

  // Called by the shell for the Android hardware back button and the iOS back
  // gesture. Returning `false` means "the web view has nothing left to go back to".
  studyflixWeb().goBack = () => {
    if (navigation.dismissOverlay()) {
      return true
    }

    const goBackEvent = new CustomEvent('native_app.go_back', { cancelable: true })
    if (!document.dispatchEvent(goBackEvent)) {
      return true
    }

    if (!router.onRootSection()) {
      router.goToRootSection()
      return true
    }

    return false
  }

  const stopInterceptingExternalLinks = interceptExternalLinks()

  return {
    router,

    destroy() {
      stopListeningForTabTaps()
      stopInterceptingExternalLinks()
      delete studyflixWeb().goBack
    },
  }
}

export { NativeAppRouter, type NativeAppNavigation }
